import { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../services/apiClient';

const emptyAmenity = { name: '', category: '', icon_key: '', sort_order: 0, is_active: true };

const AdminAmenities = ({ token }) => {
  const [amenities, setAmenities] = useState([]);
  const [form, setForm] = useState(emptyAmenity);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/admin/amenities', token);
      setAmenities(res || []);
    } catch (err) {
      setError(err.message || 'Failed to load amenities');
      setAmenities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    setEditingId(null);
    setForm(emptyAmenity);
  };

  const save = async () => {
    if (!form.name) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        sort_order: Number(form.sort_order || 0),
      };
      if (editingId) {
        await apiPut(`/api/admin/amenities/${editingId}`, payload, token);
      } else {
        await apiPost('/api/admin/amenities', payload, token);
      }
      await load();
      reset();
    } catch (err) {
      setError(err.message || 'Failed to save amenity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Properties</p>
          <h1 className="admin-page-title text-xl font-semibold text-slate-900">Amenities master</h1>
        </div>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="admin-card bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label text-sm">Amenity name</label>
            <input
              className="form-control"
              value={form.name}
              onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))}
              placeholder="Wi-Fi"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label text-sm">Category</label>
            <input
              className="form-control"
              value={form.category}
              onChange={(e) => setForm((v) => ({ ...v, category: e.target.value }))}
              placeholder="Comfort"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label text-sm">Icon key</label>
            <input
              className="form-control"
              value={form.icon_key}
              onChange={(e) => setForm((v) => ({ ...v, icon_key: e.target.value }))}
              placeholder="wifi"
            />
          </div>
          <div className="col-md-2">
            <label className="form-label text-sm">Sort</label>
            <input
              type="number"
              className="form-control"
              value={form.sort_order}
              onChange={(e) => setForm((v) => ({ ...v, sort_order: Number(e.target.value) }))}
            />
          </div>
          <div className="col-md-3 d-flex align-items-center gap-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="amenity-active"
              checked={form.is_active}
              onChange={(e) => setForm((v) => ({ ...v, is_active: e.target.checked }))}
            />
            <label htmlFor="amenity-active" className="form-check-label text-sm">
              Active
            </label>
          </div>
          <div className="col-12 d-flex gap-2">
            <button className="btn btn-primary" disabled={saving} onClick={save}>
              {saving ? 'Saving...' : editingId ? 'Update amenity' : 'Add amenity'}
            </button>
            {editingId && (
              <button className="btn btn-outline-secondary" onClick={reset}>
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Icon</th>
                <th>Sort</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && !amenities.length && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No amenities yet
                  </td>
                </tr>
              )}
              {amenities.map((a) => (
                <tr key={a.id}>
                  <td className="fw-semibold">{a.name}</td>
                  <td className="text-muted small">{a.category || '--'}</td>
                  <td className="text-muted small">{a.icon_key || '--'}</td>
                  <td>{a.sort_order ?? 0}</td>
                  <td>
                    <span className={`badge ${a.is_active ? 'bg-success-subtle text-success' : 'bg-secondary'}`}>
                      {a.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="text-end">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setEditingId(a.id);
                        setForm({
                          name: a.name || '',
                          category: a.category || '',
                          icon_key: a.icon_key || '',
                          sort_order: a.sort_order || 0,
                          is_active: a.is_active ?? true,
                        });
                      }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminAmenities;
